"use client";

import { useEffect, useState, useSyncExternalStore } from "react";

const TYPE_MS = 70;
const DELETE_MS = 42;
const HOLD_MS = 1800;

type Phase = "type" | "hold" | "delete";

function subscribeReducedMotion(onChange: () => void) {
  const media = window.matchMedia("(prefers-reduced-motion: reduce)");

  media.addEventListener("change", onChange);

  return () => media.removeEventListener("change", onChange);
}

function getReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function TypeCycle({
  words,
}: {
  words: readonly [string, ...string[]];
}) {
  const reduceMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotion,
    () => false,
  );
  const [index, setIndex] = useState(0);
  const [length, setLength] = useState(words[0].length);
  const [phase, setPhase] = useState<Phase>("hold");

  useEffect(() => {
    if (reduceMotion) {
      return;
    }

    const word = words[index];
    const delay =
      phase === "hold" ? HOLD_MS : phase === "delete" ? DELETE_MS : TYPE_MS;

    const id = window.setTimeout(() => {
      if (phase === "type") {
        if (length < word.length) {
          setLength(length + 1);
        } else {
          setPhase("hold");
        }

        return;
      }

      if (phase === "hold") {
        setPhase("delete");

        return;
      }

      if (length > 0) {
        setLength(length - 1);

        return;
      }

      setIndex((index + 1) % words.length);
      setPhase("type");
    }, delay);

    return () => window.clearTimeout(id);
  }, [index, length, phase, reduceMotion, words]);

  const text = reduceMotion ? words[0] : words[index].slice(0, length);

  return (
    <span className="text-accent">
      <span className="sr-only">{words[0]}</span>
      <span aria-hidden="true">
        {text}
        {reduceMotion ? null : <span className="caret">_</span>}
      </span>
    </span>
  );
}
